import React, { memo, ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';

import { Box } from 'src/components/Box';
import { Text } from 'src/components/Text';
import { Tooltip } from 'src/components/Tooltip';
import { FontSizes } from 'src/design-system/style-types';
import { useResizeObserver } from 'src/hooks/useResizeObserver/useResizeObserver';
import { middleTruncationHelper, truncateName } from 'src/utils/middleTruncationHelper/utils';

import { DefaultProps as TextProps } from '../Text/Text';

interface Props extends Partial<TextProps> {
  text: string;
  type?: 'file' | 'domain';
  showTooltip?: boolean;
  delayDuration?: number;
  suffix?: ReactNode;
  prefix?: ReactNode;
  size?: FontSizes;
  disableTooltipOnClick?: boolean;
  enableRecalculateWidth?: boolean;
  splitRatio?: number;
  j?: 'flex-start' | 'flex-end' | 'space-between' | 'space-around' | 'center';
}

// per-size character-width estimates, used only as a fallback when no
// canvas 2d context is available to measure the text for real
const sizeCharWidthEstimates: Record<string, number> = {
  xs: 7,
  sm: 8,
  md: 9,
  lg: 10,
  xl: 13,
  '2xl': 15,
  '3xl': 20,
  '4xl': 24,
  '5xl': 34,
};

const noop = () => {};

/**
 * Same as MiddleTruncate, but instead of budgeting the text with a fixed
 * characters-per-pixel estimate per font size (which leaves visible dead
 * space next to wide-weight fonts and clips narrow ones), it measures the
 * actual rendered font with a canvas 2d context and binary-searches the
 * longest slice that fits the real available width. The fit is recomputed
 * when webfonts finish loading and when the prefix/suffix change size.
 */
export const _MiddleTruncateNew = ({
  text,
  showTooltip = true,
  delayDuration = 400,
  suffix,
  prefix,
  size = 'sm',
  disableTooltipOnClick = true,
  enableRecalculateWidth = true,
  splitRatio = -10,
  j = 'flex-start',
  ...textProps
}: Props) => {
  const containerRef = useRef<HTMLElement | null>(null);
  const suffixRef = useRef<HTMLElement | null>(null);
  const prefixRef = useRef<HTMLElement | null>(null);
  const textRef = useRef<HTMLElement | null>(null);
  const measureCtxRef = useRef<CanvasRenderingContext2D | null>(null);

  const [width, setWidth] = useState(0);
  const [truncatedName, setTruncatedName] = useState(text);
  const [fontsLoaded, setFontsLoaded] = useState(false);

  const recalculateWidth = useCallback(
    () => {
      const prefixWidth = prefixRef.current?.offsetWidth ?? 0;
      const suffixWidth = suffixRef.current?.offsetWidth ?? 0;

      if (containerRef.current?.offsetWidth) {
        setWidth(containerRef.current?.offsetWidth - prefixWidth - suffixWidth);
      }
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useLayoutEffect(() => {
    recalculateWidth();
  }, [recalculateWidth]);

  useResizeObserver({
    elRef: containerRef,
    onResizeCallback: enableRecalculateWidth ? recalculateWidth : noop,
  });

  // the suffix/prefix can appear or change size after mount (e.g. an
  // async visibility-gated icon button), so keep the text budget in sync
  useResizeObserver({ elRef: suffixRef, onResizeCallback: recalculateWidth });
  useResizeObserver({ elRef: prefixRef, onResizeCallback: recalculateWidth });

  // measure a string with the exact font of the rendered text node;
  // returns null when measurement is unavailable
  const measureString = useCallback((value: string) => {
    if (!value) {
      return 0;
    }

    const el = textRef.current;

    if (!el || typeof document === 'undefined') {
      return null;
    }

    if (!measureCtxRef.current) {
      measureCtxRef.current = document.createElement('canvas').getContext('2d');
    }

    const ctx = measureCtxRef.current;

    if (!ctx) {
      return null;
    }

    const computed = window.getComputedStyle(el);
    // the computed font shorthand may contain line-height, which the
    // canvas font setter rejects; rebuild it from the longhands instead
    ctx.font = [
      computed.fontStyle,
      computed.fontVariant,
      computed.fontWeight,
      computed.fontSize,
      computed.fontFamily,
    ].join(' ');

    let letterSpacing = parseFloat(computed.letterSpacing as string);

    if (!Number.isFinite(letterSpacing)) {
      letterSpacing = 0;
    }

    // add the letter-spacing advance per character manually (canvas
    // measureText ignores it), matching how layout counts the
    // trailing spacing after the last character
    return ctx.measureText(value).width + letterSpacing * value.length;
  }, []);

  const fitText = useCallback(() => {
    if (!text) {
      setTruncatedName('');

      return;
    }

    const fullWidth = measureString(text);

    if (fullWidth === null) {
      // no canvas measurement available (e.g. jsdom): fall back to the
      // original character-count estimate
      setTruncatedName(
        truncateName({ text, width, splitRatio, maxCharactersRatio: sizeCharWidthEstimates[size ?? 'sm'] })
      );

      return;
    }

    // nothing to measure yet (or an unmeasured/zero-width container):
    // leave the text untruncated, matching the original behaviour
    if (!width || splitRatio === 1 || fullWidth <= width) {
      setTruncatedName(text);

      return;
    }

    const { firstSection, secondSection } = middleTruncationHelper({ text, splitRatio });
    let low = 0;
    let high = firstSection.length;
    let best = 0;

    // the candidate width grows with the slice length, so a binary search
    // finds the longest rendering that fits the measured budget
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const candidateWidth = measureString(`${firstSection.slice(0, mid)}...${secondSection}`) ?? Infinity;

      if (candidateWidth <= width) {
        best = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    setTruncatedName(`${firstSection.slice(0, best)}...${secondSection}`);
  }, [text, width, size, splitRatio, measureString]);

  useLayoutEffect(() => {
    fitText();
  }, [fitText]);

  // fallback font metrics measured before the webfont loads are usually
  // wrong, so re-fit once document.fonts reports the faces are ready
  useEffect(() => {
    if (typeof document === 'undefined' || !document.fonts) {
      return;
    }

    let active = true;

    document.fonts.ready
      .then(() => {
        if (active) {
          setFontsLoaded(true);
        }
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, []);

  useLayoutEffect(() => {
    if (fontsLoaded) {
      fitText();
    }
  }, [fitText, fontsLoaded]);

  return (
    <Box innerRef={containerRef} a="center" wrap="nowrap" j={j}>
      <div
        css={css`
          display: flex;
          align-items: center;
        `}
      >
        {prefix && (
          <Box innerRef={prefixRef} a="center" w="auto">
            {prefix}
          </Box>
        )}

        <Box a="center" w="auto" minW="0" wrap="nowrap">
          {showTooltip && truncatedName !== text ? (
            <Tooltip
              disableTooltipOnClick={disableTooltipOnClick}
              placement="top-start"
              delayDuration={delayDuration}
              button={
                <Box a="flex-start" wrap="nowrap" minW="0">
                  <Text innerRef={textRef} ariaLabel={text} {...textProps} size={size} fullWidth whiteSpace="nowrap">
                    {truncatedName}
                  </Text>
                </Box>
              }
            >
              <Text size="sm" color="light">
                {text}
              </Text>
            </Tooltip>
          ) : (
            <Text innerRef={textRef} ariaLabel={text} {...textProps} size={size} fullWidth whiteSpace="nowrap">
              {truncatedName}
            </Text>
          )}
        </Box>
      </div>

      {suffix && (
        <Box innerRef={suffixRef} a="center" w="auto">
          {suffix}
        </Box>
      )}
    </Box>
  );
};

export const MiddleTruncateNew = memo(_MiddleTruncateNew);
